import React, { useEffect, useState, useRef, useCallback } from "react";
import PaymentTablePure from "./PaymentTablePure.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import { fetchPaymentData, fetchAllPaymentData } from "../../services/payment-service.js";
import DoubleCalendar from "../../components/comman/dataPicker.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import Modal from "../../components/comman/modal.js";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";
import Toast from "../../components/Toast/toast.js";
import Button from "../../components/comman/button.js";
import Dropdown from "../../components/comman/dropdown.js";
import DropdownMenuItem from "../../components/comman/dropdownMenuItem.js";
import { exportPaymentsToCSV, exportPaymentsToExcel } from "../../utils/paymentExportUtils.js";
import calendar from "../../assets/calendar.svg";

const Payments = () => {
    const { language } = useLanguage();
    const today = new Date();
    const defaultFrom = new Date(today.getFullYear(), today.getMonth(), 1);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
    const [dateFrom, setDateFrom] = useState(defaultFrom);
    const [dateTo, setDateTo] = useState(today);
    const [comprehensiveBreakdown, setComprehensiveBreakdown] = useState(null);
    const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false);
    const [error, setError] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
    const isFetchingPayments = useRef(false);
    const toastRef = useRef(null);

    const formatDate = (date) => {
        if (!date) return null;
        return (
            date.getFullYear() +
            "-" +
            String(date.getMonth() + 1).padStart(2, "0") +
            "-" +
            String(date.getDate()).padStart(2, "0")
        );
    };

    const loadPage = useCallback(async (from, to) => {
        if (isFetchingPayments.current) return; // Prevent duplicate initial loads
        isFetchingPayments.current = true;
        setIsLoading(true);
        setError(null);
        try {
            const formattedFrom = typeof from === "string" ? from : formatDate(from);
            const formattedTo = typeof to === "string" ? to : formatDate(to);
            const limit = 20;
            const response = await fetchPaymentData(formattedFrom, formattedTo, limit, 0);

            const flattened = [];
            if (response?.paymentTypeGroups && Array.isArray(response.paymentTypeGroups)) {
                response.paymentTypeGroups.forEach((group) => {
                    const paymentType = group?.paymentType;
                    if (Array.isArray(group?.orderDetails)) {
                        group.orderDetails.forEach((order) => {
                            flattened.push({ ...order, paymentType });
                        });
                    }
                });
            }

            setComprehensiveBreakdown(response?.comprehensiveBreakdown || null);
            setError(null);
            setRows(flattened);
        } catch (e) {
            setRows([]);
            setComprehensiveBreakdown(null);
            setError(e);
        } finally {
            setIsLoading(false);
            isFetchingPayments.current = false;
        }
    }, []);

    const toggleDatePicker = () => setIsDatePickerVisible((v) => !v);
    const handleDateSelection = (selectedDates) => {
        if (selectedDates && selectedDates.length > 0) {
            const firstDate = new Date(selectedDates[0]);
            const lastDate = new Date(selectedDates[selectedDates.length - 1]);

            setDateFrom(firstDate);
            setDateTo(lastDate);
            setIsDatePickerVisible(false);
            loadPage(firstDate, lastDate);
        }
    };

    const handleExport = async (format) => {
        if (!dateFrom || !dateTo) {
            toastRef.current?.addToast(
                getTranslation('payments.selectDateRange', language) || "Please select a date range",
                "error"
            );
            setIsExportDropdownOpen(false);
            return;
        }

        setIsExporting(true);
        setIsExportDropdownOpen(false);
        try {
            const formattedFrom = formatDate(dateFrom);
            const formattedTo = formatDate(dateTo);

            // Fetch all payment data
            const allPayments = await fetchAllPaymentData(formattedFrom, formattedTo);

            if (!allPayments || allPayments.length === 0) {
                toastRef.current?.addToast(
                    getTranslation('payments.noDataToExport', language) || "No payment data available for export",
                    "warning"
                );
                setIsExporting(false);
                return;
            }

            // Export based on format
            if (format === 'csv') {
                exportPaymentsToCSV(allPayments, formattedFrom, formattedTo, language, getTranslation);
            } else if (format === 'excel') {
                exportPaymentsToExcel(allPayments, formattedFrom, formattedTo, language, getTranslation);
            }

            toastRef.current?.addToast(
                getTranslation('payments.exportSuccess', language) || "Report exported successfully",
                "success"
            );
        } catch (error) {
            console.error('Export error:', error);
            toastRef.current?.addToast(
                getTranslation('payments.exportError', language) || "Failed to export report",
                "error"
            );
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        if (dateFrom && dateTo) {
            loadPage(dateFrom, dateTo);
        }
    }, [dateFrom, dateTo, loadPage]);

    return (
        <div className="pl-2">
            <h1 className="text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9]">{getTranslation('menuItems.payments', language)}</h1>
            <div className="space-y-6">

            {/* Date Picker Controls - Compact & Elegant */}
            <div className="rounded-lg border border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#161b22] shadow-sm p-4">
                <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={toggleDatePicker}
                            className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border h-9 px-4 py-2 ${
                                isDatePickerVisible 
                                    ? 'bg-white dark:bg-[#1c2128] border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#1c2128] text-gray-900 dark:text-[#c9d1d9]' 
                                    : 'border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#0d1117] hover:bg-gray-50 dark:hover:bg-[#161b22] text-gray-900 dark:text-[#c9d1d9]'
                            }`}
                            aria-expanded={isDatePickerVisible}
                            aria-haspopup="dialog"
                        >
                            <img className="mr-2 h-4 w-4 dark:invert dark:opacity-80" src={calendar || ''} alt="" aria-hidden="true"/>
                            <span className="hidden sm:inline">{getTranslation('payments.selectDate', language)}</span>
                            <span className="sm:hidden">{getTranslation('payments.selectDateShort', language)}</span>
                        </button>

                        {/* Export Dropdown */}
                        <Dropdown
                            isOpen={isExportDropdownOpen}
                            onOpenChange={setIsExportDropdownOpen}
                            position="bottom-right"
                            trigger={
                                <Button
                                    variant="outline"
                                    size="md"
                                    disabled={!dateFrom || !dateTo || isExporting}
                                    loading={isExporting}
                                >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    {getTranslation('payments.export', language) || getTranslation('payments.exportReport', language) || "Export"}
                                    <svg 
                                        className={`h-4 w-4 transition-transform ${isExportDropdownOpen ? 'rotate-180' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </Button>
                            }
                        >
                            <div className="py-1 min-w-[160px]">
                                <DropdownMenuItem
                                    onClick={() => handleExport('excel')}
                                    disabled={isExporting}
                                >
                                    <div className="flex items-center gap-2">
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>{getTranslation('payments.exportExcel', language) || "Excel (XLSX)"}</span>
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => handleExport('csv')}
                                    disabled={isExporting}
                                >
                                    <div className="flex items-center gap-2">
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>{getTranslation('payments.exportCSV', language) || "CSV"}</span>
                                    </div>
                                </DropdownMenuItem>
                            </div>
                        </Dropdown>
                    </div>

                    {dateFrom && dateTo && (
                        <div className="flex items-center gap-3 text-sm">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 rounded-md">
                                <span className="text-xs font-medium text-gray-600 dark:text-[#8b949e]">
                                    {getTranslation('common.from', language)}:
                                </span>
                                <span className="font-mono font-semibold text-gray-700 dark:text-gray-400">
                                    {formatDate(dateFrom)}
                                </span>
                            </div>
                            <div className="text-gray-500 dark:text-[#8b949e]">→</div>
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 rounded-md">
                                <span className="text-xs font-medium text-gray-600 dark:text-[#8b949e]">
                                    {getTranslation('common.to', language)}:
                                </span>
                                <span className="font-mono font-semibold text-gray-700 dark:text-gray-400">
                                    {formatDate(dateTo)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Date Picker Modal */}
            <Modal
                isOpen={isDatePickerVisible}
                onClose={() => setIsDatePickerVisible(false)}
                title={getTranslation('payments.selectDate', language)}
                size="4xl"
            >
                        <DoubleCalendar
                            onClose={() => setIsDatePickerVisible(false)}
                            onDateSelect={handleDateSelection}
                            initialDateFrom={dateFrom}
                            initialDateTo={dateTo}
                        />
            </Modal>

            {comprehensiveBreakdown && (
                <div className="rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white dark:from-[#161b22] via-white dark:via-[#161b22] to-gray-50 dark:to-[#0d1117] shadow-lg overflow-hidden">
                    {/* Header with toggle - Mobile Optimized */}
                    <div 
                        className="p-4 sm:p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1c2128] active:bg-white dark:active:bg-[#1c2128] transition-all duration-200 touch-manipulation"
                        onClick={() => setIsBreakdownExpanded(!isBreakdownExpanded)}
                        style={{ minHeight: '44px' }} // Ensure minimum touch target
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-[#c9d1d9] leading-tight">
                                        {getTranslation('payments.comprehensiveBreakdown', language)}
                                    </h2>
                                    <div className="flex-shrink-0 p-1 rounded-full bg-white dark:bg-gray-800/30">
                                        <svg 
                                            className={`w-5 h-5 sm:w-6 sm:h-6 text-gray-700 dark:text-gray-400 transition-transform duration-300 ease-out ${isBreakdownExpanded ? 'rotate-180' : ''}`} 
                                            fill="none" 
                                            viewBox="0 0 24 24" 
                                            stroke="currentColor"
                                            strokeWidth={2.5}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-sm sm:text-base text-gray-600 dark:text-[#b1bac4] leading-relaxed pr-2 font-medium">
                                    {getTranslation('payments.breakdownDescription', language)}
                                </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <div className="text-2xl sm:text-3xl font-bold text-gray-700 dark:text-gray-400 leading-none mb-1">
                                    {(comprehensiveBreakdown.totalOrderPrice ?? 0).toLocaleString()} Դ
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600 dark:text-[#8b949e] font-medium">
                                    {getTranslation('payments.totalOrderPrice', language)}
                                </div>
                            </div>
                        </div>
                        
                        {/* Quick preview metrics when collapsed */}
                        {!isBreakdownExpanded && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-[#30363d]">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="text-center p-2 rounded-lg bg-white dark:bg-[#0d1117] border dark:border-[#30363d]">
                                        <div className="text-lg sm:text-xl font-bold text-gray-700 dark:text-gray-400 leading-none">
                                            {(comprehensiveBreakdown.netCashChefKeeps ?? 0).toLocaleString()} Դ
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-[#8b949e] mt-1 font-medium">
                                            {getTranslation('payments.netCashChefKeeps', language)}
                                        </div>
                                    </div>
                                    <div className="text-center p-2 rounded-lg bg-white dark:bg-[#0d1117] border dark:border-[#30363d]">
                                        <div className="text-lg sm:text-xl font-bold text-gray-700 dark:text-gray-400 leading-none">
                                            {(comprehensiveBreakdown.totalChefEarned ?? 0).toLocaleString()} Դ
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-[#8b949e] mt-1 font-medium">
                                            {getTranslation('payments.chef', language).replace(':', '')}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center mt-3">
                                    <div className="text-xs text-gray-700 dark:text-gray-400 font-semibold flex items-center justify-center gap-1">
                                        {getTranslation('payments.showMore', language)}
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Collapsible content */}
                    {isBreakdownExpanded && (
                        <div className="px-4 sm:px-6 pb-6 space-y-6">
                            {/* Key Financial Metrics */}
                            <div className="space-y-4">
                                {/* Cash vs Non-Cash */}
                                <div className="bg-gradient-to-r from-white dark:from-[#0d1117] to-gray-50 dark:to-[#0d1117] rounded-xl p-4 sm:p-5 border border-gray-200 dark:border-[#30363d] shadow-sm">
                                    <div className="text-gray-700 dark:text-[#b1bac4] text-sm sm:text-base mb-3 font-semibold">{getTranslation('payments.cashNonCash', language)}</div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="text-center">
                                            <div className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-400 leading-none mb-1">
                                                {(comprehensiveBreakdown.totalCashAmount ?? 0).toLocaleString()} Դ
                                            </div>
                                            <div className="text-sm sm:text-base text-gray-600 dark:text-[#8b949e] font-medium">{getTranslation('payments.cash', language)}</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-400 leading-none mb-1">
                                                {(comprehensiveBreakdown.totalNonCashAmount ?? 0).toLocaleString()} Դ
                                            </div>
                                            <div className="text-sm sm:text-base text-gray-600 dark:text-[#8b949e] font-medium">{getTranslation('payments.nonCash', language)}</div>
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-[#b1bac4] mt-3 p-3 bg-white dark:bg-[#0d1117] rounded-lg leading-relaxed font-medium">
                                        {getTranslation('payments.cashNonCashDesc', language)}
                                    </div>
                                </div>

                                {/* Chef vs Platform Earnings */}
                                <div className="bg-gradient-to-r from-white dark:from-[#0d1117] to-gray-50 dark:to-[#0d1117] rounded-xl p-4 sm:p-5 border border-gray-200 dark:border-[#30363d] shadow-sm">
                                    <div className="text-gray-700 dark:text-[#b1bac4] text-sm sm:text-base mb-3 font-semibold">{getTranslation('payments.chefPlatform', language)}</div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="text-center">
                                            <div className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-400 leading-none mb-1">
                                                {(comprehensiveBreakdown.totalChefEarned ?? 0).toLocaleString()} Դ
                                            </div>
                                            <div className="text-sm sm:text-base text-gray-600 dark:text-[#8b949e] font-medium">{getTranslation('payments.chef', language).replace(':', '')}</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-400 leading-none mb-1">
                                                {(comprehensiveBreakdown.totalPlatformEarned ?? 0).toLocaleString()} Դ
                                            </div>
                                            <div className="text-sm sm:text-base text-gray-600 dark:text-[#8b949e] font-medium">{getTranslation('payments.platform', language).replace(':', '')}</div>
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-[#b1bac4] mt-3 p-3 bg-white dark:bg-[#0d1117] rounded-lg leading-relaxed font-medium">
                                        {getTranslation('payments.chefPlatformDesc', language)}
                                    </div>
                                </div>

                                {/* Net Cash Chef Keeps - Highlighted */}
                                <div className="bg-gradient-to-r from-gray-50 dark:from-gray-900/30 to-gray-50 dark:to-gray-900/30 rounded-xl p-4 sm:p-5 border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                                    <div className="text-gray-800 dark:text-gray-300 text-sm sm:text-base mb-2 font-semibold">{getTranslation('payments.netCashChefKeeps', language)}</div>
                                    <div className="text-center">
                                        <div className="text-3xl sm:text-4xl font-bold text-gray-700 dark:text-gray-400 leading-none mb-2">
                                            {(comprehensiveBreakdown.netCashChefKeeps ?? 0).toLocaleString()} Դ
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-800 dark:text-gray-300 bg-white dark:bg-gray-900/40 p-3 rounded-lg leading-relaxed font-medium">
                                        {getTranslation('payments.netCashChefKeepsDesc', language)}
                                    </div>
                                </div>
                            </div>

                            {/* Transfer Breakdown */}
                            <div className="border-t border-gray-200 dark:border-[#30363d] pt-6">
                                    <h3 className="text-lg sm:text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9] flex items-center gap-2">
                                    <span className="text-gray-700 dark:text-gray-400">💸</span>
                                    {getTranslation('payments.transferBreakdown', language)}
                                </h3>
                                <div className="space-y-4">
                                    {/* Regular Transfer */}
                                    <div className="bg-gradient-to-r from-gray-50 dark:from-gray-900/30 to-gray-50 dark:to-gray-900/30 rounded-xl p-4 sm:p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                                        <div className="text-gray-800 dark:text-gray-300 text-sm sm:text-base mb-2 font-semibold">{getTranslation('payments.regularTransfer', language)}</div>
                                        <div className="text-center mb-3">
                                            <div className="text-2xl sm:text-3xl font-bold text-gray-700 dark:text-gray-400 leading-none">
                                                {(comprehensiveBreakdown.regularTransferAmount ?? 0).toLocaleString()} Դ
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-800 dark:text-gray-300 bg-white dark:bg-gray-900/40 p-3 rounded-lg leading-relaxed font-medium">
                                            {getTranslation('payments.regularTransferDesc', language)}
                                        </div>
                                    </div>

                                    {/* Discount Compensation */}
                                    <div className="bg-gradient-to-r from-gray-50 dark:from-gray-900/30 to-gray-50 dark:to-gray-900/30 rounded-xl p-4 sm:p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                                        <div className="text-gray-800 dark:text-gray-300 text-sm sm:text-base mb-2 font-semibold">{getTranslation('payments.discountCompensation', language)}</div>
                                        <div className="text-center mb-3">
                                            <div className="text-2xl sm:text-3xl font-bold text-gray-700 dark:text-gray-400 leading-none">
                                                {(comprehensiveBreakdown.separateDiscountCompensation ?? 0).toLocaleString()} Դ
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-800 dark:text-gray-300 bg-white dark:bg-gray-900/40 p-3 rounded-lg leading-relaxed font-medium">
                                            {getTranslation('payments.discountCompensationDesc', language)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Detailed Financial Data */}
                            <div className="border-t border-gray-200 dark:border-[#30363d] pt-6">
                                <h3 className="text-lg sm:text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9] flex items-center gap-2">
                                    <span className="text-slate-500 dark:text-[#8b949e]">📊</span>
                                    {getTranslation('payments.detailedFinancialData', language)}
                                </h3>
                                <div className="grid grid-cols-1 gap-3">
                                    {/* Actual Cash Received */}
                                    <div className="bg-white dark:bg-[#0d1117] rounded-lg p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="text-gray-600 dark:text-[#b1bac4] text-sm font-medium mb-1">{getTranslation('payments.actualCashReceived', language)}</div>
                                                <div className="text-xs text-gray-500 dark:text-[#8b949e] leading-tight font-medium">{getTranslation('payments.actualCashReceivedDesc', language)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-bold text-lg text-gray-700 dark:text-gray-400">{(comprehensiveBreakdown.actualCashReceivedByChef ?? 0).toLocaleString()} Դ</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Non-Cash Orders Amount */}
                                    <div className="bg-white dark:bg-[#0d1117] rounded-lg p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="text-gray-600 dark:text-[#b1bac4] text-sm font-medium mb-1">{getTranslation('payments.nonCashOrdersAmount', language)}</div>
                                                <div className="text-xs text-gray-500 dark:text-[#8b949e] leading-tight font-medium">{getTranslation('payments.nonCashOrdersAmountDesc', language)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-bold text-lg text-gray-700 dark:text-gray-400">{(comprehensiveBreakdown.nonCashOrdersAmount ?? 0).toLocaleString()} Դ</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Fee Compensation From Cash */}
                                    <div className="bg-white dark:bg-[#0d1117] rounded-lg p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="text-gray-600 dark:text-[#b1bac4] text-sm font-medium mb-1">{getTranslation('payments.feeCompensationFromCash', language)}</div>
                                                <div className="text-xs text-gray-500 dark:text-[#8b949e] leading-tight font-medium">{getTranslation('payments.feeCompensationFromCashDesc', language)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-bold text-lg text-gray-700 dark:text-gray-400">{(comprehensiveBreakdown.feeCompensationFromCash ?? 0).toLocaleString()} Դ</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Total Discounts Applied */}
                                    <div className="bg-white dark:bg-[#0d1117] rounded-lg p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="text-gray-600 dark:text-[#b1bac4] text-sm font-medium mb-1">{getTranslation('payments.totalDiscountsApplied', language)}</div>
                                                <div className="text-xs text-gray-500 dark:text-[#8b949e] leading-tight font-medium">{getTranslation('payments.totalDiscountsAppliedDesc', language)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-bold text-lg text-gray-700 dark:text-gray-400">-{(comprehensiveBreakdown.totalDiscountsApplied ?? 0).toLocaleString()} Դ</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Total Delivery Fees */}
                                    <div className="bg-white dark:bg-[#0d1117] rounded-lg p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="text-gray-600 dark:text-[#b1bac4] text-sm font-medium mb-1">{getTranslation('payments.totalDeliveryFees', language)}</div>
                                                <div className="text-xs text-gray-500 dark:text-[#8b949e] leading-tight font-medium">{getTranslation('payments.totalDeliveryFeesDesc', language)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-bold text-lg text-gray-700 dark:text-gray-400">{(comprehensiveBreakdown.totalDeliveryFees ?? 0).toLocaleString()} Դ</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Total Platform Fees */}
                                    <div className="bg-white dark:bg-[#0d1117] rounded-lg p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="text-gray-600 dark:text-[#b1bac4] text-sm font-medium mb-1">{getTranslation('payments.totalPlatformFees', language)}</div>
                                                <div className="text-xs text-gray-500 dark:text-[#8b949e] leading-tight font-medium">{getTranslation('payments.totalPlatformFeesDesc', language)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-bold text-lg text-gray-700 dark:text-gray-400">{(comprehensiveBreakdown.totalPlatformFees ?? 0).toLocaleString()} Դ</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Show Less Button */}
                            <div className="text-center pt-6 border-t border-gray-200 dark:border-[#30363d]">
                                <button
                                    onClick={() => setIsBreakdownExpanded(false)}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-[#b1bac4] hover:text-gray-900 dark:hover:text-[#c9d1d9] bg-white dark:bg-[#0d1117] hover:bg-gray-50 dark:hover:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg transition-all duration-200 touch-manipulation"
                                    style={{ minHeight: '44px' }}
                                >
                                    <svg className="w-4 h-4 rotate-180 text-gray-600 dark:text-[#8b949e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                    {getTranslation('payments.showLess', language)}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}



                        {/* Mobile cards */}
            <div className="block sm:hidden mb-8" id="payments-mobile-container">
                {isLoading && rows.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                        <LoadingSpinner />
                    </div>
                ) : error ? (
                    <ErrorDisplay
                        error={error}
                        onRetry={() => loadPage(dateFrom, dateTo, 0)}
                    />
                ) : rows.length > 0 ? (
                    <div className="space-y-4 max-h-[70vh] overflow-auto">
                        {rows.map((item, index) => (
                            <div key={index} className="rounded-xl border-2 border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#161b22] shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
                                {/* Header */}
                                <div className="bg-gradient-to-r from-gray-50 dark:from-gray-900/30 to-gray-50 dark:to-gray-900/30 p-4 border-b border-gray-200 dark:border-[#30363d]">
                                    <div className="flex items-center justify-between mb-2">
                                        <a 
                                            href={`/orders/${item?.orderId}`} 
                                            className="text-base font-bold font-mono text-gray-700 dark:text-gray-400 hover:underline touch-manipulation"
                                            style={{ minHeight: '44px', display: 'flex', alignItems: 'center' }}
                                        >
                                            {item?.orderNumber}
                                        </a>
                                        <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-white dark:bg-gray-800/50 text-gray-800 dark:text-gray-400 border dark:border-gray-700">
                                                {item?.paymentType}
                                            </span>
                                            {item?.promoCodeApplied && (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white dark:bg-gray-800/50 text-gray-700 dark:text-gray-400 border dark:border-gray-700">
                                                    🎟️ Promo
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-gray-700 dark:text-gray-400 leading-none">
                                            {(item?.totalPrice ?? 0).toLocaleString()} Դ
                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-[#8b949e] font-medium mt-1">
                                            {getTranslation('payments.amount', language)}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Content */}
                                <div className="p-4 space-y-3">
                                    {/* Chef vs Platform */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="text-center p-3 bg-white dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700">
                                            <div className="text-lg font-bold text-gray-700 dark:text-gray-400 leading-none">
                                                {(item?.chefEarning ?? 0).toLocaleString()} Դ
                                            </div>
                                            <div className="text-xs text-gray-800 dark:text-gray-300 font-medium mt-1">
                                                {getTranslation('payments.chef', language).replace(':', '')}
                                            </div>
                                        </div>
                                        <div className="text-center p-3 bg-white dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700">
                                            <div className="text-lg font-bold text-gray-700 dark:text-gray-400 leading-none">
                                                {(item?.platformEarning ?? 0).toLocaleString()} Դ
                                            </div>
                                            <div className="text-xs text-gray-800 dark:text-gray-300 font-medium mt-1">
                                                Platform
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Platform Fee */}
                                    <div className="flex items-center justify-between p-3 bg-white dark:bg-[#0d1117] rounded-lg border dark:border-[#30363d]">
                                        <span className="text-sm font-medium text-gray-600 dark:text-[#b1bac4]">
                                            {getTranslation('payments.platformFee', language)}
                                        </span>
                                        <span className="text-base font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                            {(item?.platformFee ?? 0).toLocaleString()} Դ
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {/* Loading indicator for infinite scroll */}
                        {isLoading && rows.length > 0 && (
                            <div className="flex items-center justify-center py-6">
                                <LoadingSpinner />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="rounded-xl bg-white dark:bg-[#0d1117] p-4 mb-4 border dark:border-[#30363d]">
                            <svg className="h-8 w-8 text-gray-400 dark:text-[#8b949e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-[#c9d1d9] mb-2">{getTranslation('payments.noTransactionsShort', language)}</h3>
                        <p className="text-base text-gray-600 dark:text-[#b1bac4] leading-relaxed max-w-sm font-medium">{getTranslation('payments.noTransactions', language)}</p>
                    </div>
                )}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block mb-8">
                {isLoading && rows.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                        <LoadingSpinner />
                    </div>
                ) : error ? (
                    <ErrorDisplay
                        error={error}
                        onRetry={() => loadPage(dateFrom, dateTo, 0)}
                    />
                ) : (
                    <PaymentTablePure rows={rows} isLoading={isLoading} containerId="payments-table-container" />
                )}
            </div>
            </div>
            <Toast ref={toastRef} />
        </div>
    );
};

export default Payments;
